import { createClient } from '@/utils/supabase/client'
import type {
  Cart,
  CartInsert,
  CartLineItem,
  CartLineItemInsert,
  CartWithCartLineItems,
} from '@/utils/supabase/scanReachTypes'

export interface AddToCartParams {
  micrositeId: string
  quantity: number
  unitPrice: number
  qrDataUrl: string
  internalProductId: number
}

const DEFAULT_CURRENCY = 'USD'

/**
 * Get or create a cart for the current user
 */
export async function getOrCreateCart(): Promise<Cart> {
  const supabase = createClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('User must be authenticated to access cart')
  }

  // Try to get existing cart
  const { data: existingCart, error: fetchError } = await supabase
    .from('carts')
    .select('*')
    .eq('user_id', user.id)
    .eq('cart_status', 'open')
    .single()

  if (existingCart && !fetchError) {
    return existingCart
  }

  const newCartPayload: CartInsert = {
    user_id: user.id,
    id_address_delivery: 0,
    id_address_invoice: 0,
    id_carrier: 0,
    id_guest: 0,
    id_lang: 0,
    id_shop: 0,
    mobile_theme: 0,
    recyclable: 0,
    allow_seperated_package: 0,
    gift: 0,
    gift_message: null,
    delivery_option: '',
    currency: DEFAULT_CURRENCY,
    checkout_session_data: null,
    secure_key: '',
    cart_status: 'open',
  }

  // Create new cart if none exists
  const { data: newCart, error: createError } = await supabase
    .from('carts')
    .insert(newCartPayload)
    .select()
    .single()

  if (createError) {
    throw new Error(`Failed to create cart: ${createError.message}`)
  }

  return newCart
}

/**
 * Add an item to the cart
 */
export async function addToCart(params: AddToCartParams): Promise<CartLineItem> {
  const supabase = createClient()
  const cart = await getOrCreateCart()

  // Check if item already exists in cart (same product, variant, microsite)
  const { data: existingItem } = await supabase
    .from('cart_line_items')
    .select('*')
    .eq('cart_id', cart.id)
    .eq('microsite_id', params.micrositeId)
    .eq('internal_product_id', params.internalProductId)
    .single()

  if (existingItem) {
    // Update quantity if item exists
    const newQuantity = existingItem.quantity + params.quantity
    const { data: updatedItem, error } = await supabase
      .from('cart_line_items')
      .update({ quantity: newQuantity })
      .eq('id', existingItem.id)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to update cart item: ${error.message}`)
    }

    return updatedItem
  }

  const insertCartLineItemPayload: CartLineItemInsert = {
    cart_id: cart.id,
    microsite_id: params.micrositeId,
    quantity: params.quantity,
    qr_data_url: params.qrDataUrl,
    internal_product_id: params.internalProductId,
  }

  // Add new item to cart
  const { data: newItem, error } = await supabase
    .from('cart_line_items')
    .insert(insertCartLineItemPayload)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to add item to cart: ${error.message}`)
  }

  return newItem
}

/**
 * Get cart with all line items
 */
export async function getCartWithItems(): Promise<CartWithCartLineItems | null> {
  const supabase = createClient()
  const session = await supabase.auth.getSession()

  if (!session.data?.session?.access_token) {
    throw new Error('User must be authenticated to access cart')
  }

  const cart = await fetch('/api/cart', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.data.session?.access_token}`,
      'Content-Type': 'application/json',
    },
  })

  const cartData = (await cart.json()) as CartWithCartLineItems

  return cartData
}

/**
 * Update cart line item quantity
 */
export async function updateCartItemQuantity(itemId: string, quantity: number): Promise<void> {
  const supabase = createClient()

  if (quantity <= 0) {
    await removeCartItem(itemId)
    return
  }

  const { error } = await supabase
    .from('cart_line_items')
    .update({ quantity })
    .eq('id', itemId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update cart item quantity: ${error.message}`)
  }
}

/**
 * Remove an item from the cart
 */
export async function removeCartItem(itemId: string): Promise<void> {
  const supabase = createClient()

  const { error } = await supabase.from('cart_line_items').delete().eq('id', itemId)

  if (error) {
    throw new Error(`Failed to remove cart item: ${error.message}`)
  }
}

/**
 * Clear all items from the cart
 */
export async function clearCart(): Promise<void> {
  const supabase = createClient()
  const cart = await getOrCreateCart()

  const { error } = await supabase.from('cart_line_items').delete().eq('cart_id', cart.id)

  if (error) {
    throw new Error(`Failed to clear cart: ${error.message}`)
  }
}

export async function getCartItemCount(): Promise<number> {
  const supabase = createClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return 0
  }

  const { data: cart } = await supabase
    .from('carts')
    .select('id')
    .eq('user_id', user.id)
    .eq('cart_status', 'open')
    .single()

  if (!cart) {
    return 0
  }

  const { data: items } = await supabase
    .from('cart_line_items')
    .select('quantity')
    .eq('cart_id', cart.id)

  if (!items) {
    return 0
  }

  return items.reduce((total, item) => total + item.quantity, 0)
}
